//NPM
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

// UTILITY
import errorHandler from "Utils/error-handler";

// HELPERS
import { toaster } from "Helpers/toaster";
import { isEmpty, omit } from "lodash";

//STYLE
import "../parcel-tracking-modals.scss";

import { map } from "lodash";

// HOOKS
import useSession from "Hooks/session.hook";

//ANT COMPONENTS
import { Col, Row, Steps } from "antd";
import { AlertBox, Button, SelectInput } from "@life-line-lab/components-react";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import {
  ExclamationCircleOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { CmsApiResponseException } from "Utils/error-handler/api-response-exception";

interface EditRouteModalProps {
  // versionInfo: any;
  // onSubmit?: any;
  handleCancel: any;
  info: any;
}

const useFetchAvailableStations = (info) => {
  const { stations } = info?.trainLineData || {};
  const { startStation, lastStation } = info || {};

  let availableStations: any = [];
  let sortStations: any = [];
  let anyAvailableStation = true;

  const selectedStartStation = {
    index: stations?.findIndex((station) => startStation.toString() === station?.stationCode),
    details: stations?.find((station) => startStation.toString() === station?.stationCode),
  };

  const selectedLastStation = {
    index: stations?.findIndex((station) => lastStation.toString() === station?.stationCode),
    details: stations?.find((station) => lastStation.toString() === station?.stationCode),
  };

  // Stations can be reversed, that is for a MRT Line of Kwasa Damansara -> Kajang, it can also be Kajang -> Kwasa Damansara
  //   if reversed (that is, lastStation's code is less than startStation's code), the array must also be reversed
  if (lastStation < startStation) {
    sortStations = stations?.slice();
    sortStations.sort((a, b) => (a.stationCode > b.stationCode ? -1 : 1));
    selectedLastStation.index = sortStations?.length - selectedLastStation.index - 1;
  } else {
    sortStations = stations?.slice();
  }

  // Find available stations
  if (selectedLastStation?.index !== sortStations?.length - 1) {
    for (let i = selectedLastStation.index; i < sortStations?.length; i++) {
      availableStations.push(sortStations[i]);
    }
  }

  // Disable select if no available stations can be found
  if (isEmpty(availableStations)) {
    anyAvailableStation = false;
    availableStations.push(selectedLastStation?.details);
  }

  return {
    availableStations,
    selectedStartStation,
    selectedLastStation,
    anyAvailableStation,
  };
};

const EditRouteModal = ({ handleCancel, handleUpdateRoute, info }) => {
  const { t } = useTranslation();

  const { availableStations, selectedStartStation, anyAvailableStation } =
    useFetchAvailableStations(info);

  const initialInput = {
    lastStation: info?.lastStation,
  };
  const initialError = {};
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });
    setInput({ ...input, [name]: value });
  };

  const handleSubmit = () => {
    if (input?.lastStation == info?.lastStation) {
      toaster("info", "This is the current last station. Please select another station.");
    } else {
      const inputData = { ...info, lastStation: input?.lastStation };
      handleUpdateRoute(
        omit(inputData, [
          "trainLineData",
          "trolleyParcelData",
          "_rev",
          "lastKnownLocation",
          "lastKnownLocationData",
        ])
      );
    }
  };

  return (
    <div className="parcel-tracking-modal">
      <ModuleTitleBar titleText={t("title.editRoute").toUpperCase()} />
      <Row className="edit-route-modal" gutter={[0, 16]}>
        <Col>
          <Row gutter={[0, 16]}>
            <Col className="main-title" span={24}>
              {t("txt.journeyInformation")}
            </Col>
            <Col>
              <Row gutter={[8, 8]} align="middle">
                <Col span={6} className="label">
                  {t("label.journeyCode")}:
                </Col>
                <Col span={18}>{info?.journeyCode}</Col>
                <Col span={6} className="label">
                  {t("label.departingFrom")}:
                </Col>
                <Col span={18}>
                  {selectedStartStation?.details?.stationName ?? t("txt.noStationSelected")}
                </Col>
                <Col span={6} className="label">
                  {t("label.lastStation")}:
                </Col>
                <Col span={18}>
                  <SelectInput
                    showSearch
                    isRequired={true}
                    options={availableStations?.map((station) => {
                      return { label: station?.stationName, value: station?.stationCode };
                    })}
                    value={input?.lastStation}
                    name="lastStation"
                    placeholder={t("txt.selectHere")}
                    onChange={(value: string | string[]) =>
                      handleSelectChange(value, "lastStation")
                    }
                    isReadonly={!anyAvailableStation}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          {anyAvailableStation ? (
            <div className="erm-info erm-help">
              <QuestionCircleOutlined style={{ fontSize: "16px " }} />
              <div>{t("label.stationAfterLastSelect")}</div>
            </div>
          ) : (
            <div className="erm-info erm-error">
              <ExclamationCircleOutlined style={{ fontSize: "16px " }} />
              <div>{t("label.stationUnabletoSelect")}</div>
            </div>
          )}
        </Col>
      </Row>
      <div className="mt-30 right">
        {anyAvailableStation && (
          <Button
            type={"primary"}
            label={t("action.confirm")}
            onClick={() => {
              handleSubmit();
            }}
          />
        )}
        <Button
          className="cancel-btn"
          type={"default"}
          label={t("action.cancel")}
          onClick={handleCancel}
        />
      </div>
    </div>
  );
};

export default EditRouteModal;
